import {Popover} from '@mui/material';
import {Button} from '@wandb/weave/components/Button';
import {
  DraggableGrow,
  DraggableHandle,
} from '@wandb/weave/components/DraggablePopups';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {IconButton} from '@wandb/weave/components/IconButton';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useCallback, useState} from 'react';

import {
  OperatorGroupedOption,
  SelectOperatorOption,
} from '../../../../filters/common';
import {SelectOperator} from '../../../../filters/SelectOperator';

export type ScoreFilter = {
  id: string | number;
  scoreKey: string;
  operator: string;
  value: number;
};

type ScoreOption = {
  value: string;
  label: string;
};

type GroupedScoreOption = {
  label: string;
  options: ScoreOption[];
};

type ScoreFilterPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  availableScoreKeys: GroupedScoreOption[];
  scoreFilters: ScoreFilter[];
  onFiltersChange: (filters: ScoreFilter[]) => void;
};

const getNextFilterId = (filters: ScoreFilter[]): number => {
  if (filters.length === 0) {
    return 0;
  }
  const maxId = Math.max(...filters.map(f => Number(f.id) || 0));
  return maxId + 1;
};

const NUMERIC_OPERATORS: SelectOperatorOption[] = [
  {value: '(number): =', label: '=', group: 'number'},
  {value: '(number): !=', label: '≠', group: 'number'},
  {value: '(number): <', label: '<', group: 'number'},
  {value: '(number): <=', label: '≤', group: 'number'},
  {value: '(number): >', label: '>', group: 'number'},
  {value: '(number): >=', label: '≥', group: 'number'},
];

const NUMERIC_OPERATOR_OPTIONS: OperatorGroupedOption[] = [
  {
    label: 'Number',
    options: NUMERIC_OPERATORS,
  },
];

export const ScoreFilterPopover: React.FC<ScoreFilterPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  availableScoreKeys,
  scoreFilters,
  onFiltersChange,
}) => {
  const [localFilters, setLocalFilters] = useState<ScoreFilter[]>(scoreFilters);

  React.useEffect(() => {
    setLocalFilters(scoreFilters);
  }, [scoreFilters]);

  const handleAddFilter = useCallback(() => {
    const newFilter: ScoreFilter = {
      id: getNextFilterId(localFilters),
      scoreKey: '',
      operator: NUMERIC_OPERATORS[4].value,
      value: 0,
    };
    const newFilters = [...localFilters, newFilter];
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  }, [localFilters, onFiltersChange]);

  const handleRemoveFilter = useCallback(
    (id: string | number) => {
      const newFilters = localFilters.filter(f => f.id !== id);
      setLocalFilters(newFilters);
      onFiltersChange(newFilters);
    },
    [localFilters, onFiltersChange]
  );

  const handleUpdateFilter = useCallback(
    (id: string | number, updates: Partial<ScoreFilter>) => {
      const newFilters = localFilters.map(f =>
        f.id === id ? {...f, ...updates} : f
      );
      setLocalFilters(newFilters);
      onFiltersChange(newFilters);
    },
    [localFilters, onFiltersChange]
  );

  const handleRemoveAll = useCallback(() => {
    setLocalFilters([]);
    onFiltersChange([]);
    onClose();
  }, [onFiltersChange, onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            marginTop: '8px',
            overflow: 'visible',
            minWidth: '600px',
          },
        },
      }}
      onClose={onClose}
      TransitionComponent={DraggableGrow}>
      <Tailwind>
        <div className="p-12">
          <DraggableHandle>
            <div className="handle flex items-center gap-8 pb-12">
              <div className="font-semibold">Score Filters</div>
            </div>
          </DraggableHandle>
          <div className="grid grid-cols-[minmax(280px,1fr)_auto_minmax(100px,150px)_30px] gap-4">
            {localFilters.map(filter => (
              <ScoreFilterRow
                key={filter.id}
                filter={filter}
                availableScoreKeys={availableScoreKeys}
                onUpdate={handleUpdateFilter}
                onRemove={handleRemoveFilter}
              />
            ))}
          </div>
          {localFilters.length === 0 && (
            <div className="mb-8 text-moon-500">
              No score filters applied. Click "Add filter" to get started.
            </div>
          )}
          <div className="mt-8 flex items-center gap-8">
            <Button
              size="small"
              variant="ghost"
              icon="add-new"
              disabled={availableScoreKeys.length === 0}
              onClick={handleAddFilter}>
              Add filter
            </Button>
            <div className="flex-auto" />
            {localFilters.length > 0 && (
              <Button
                size="small"
                variant="ghost"
                icon="delete"
                onClick={handleRemoveAll}>
                Remove all
              </Button>
            )}
          </div>
        </div>
      </Tailwind>
    </Popover>
  );
};

type ScoreFilterRowProps = {
  filter: ScoreFilter;
  availableScoreKeys: GroupedScoreOption[];
  onUpdate: (id: string | number, updates: Partial<ScoreFilter>) => void;
  onRemove: (id: string | number) => void;
};

const ScoreFilterRow: React.FC<ScoreFilterRowProps> = ({
  filter,
  availableScoreKeys,
  onUpdate,
  onRemove,
}) => {
  const selectedScoreKey = React.useMemo(() => {
    if (!filter.scoreKey) return undefined;
    for (const group of availableScoreKeys) {
      const found = group.options.find(k => k.value === filter.scoreKey);
      if (found) return found;
    }
    return undefined;
  }, [filter.scoreKey, availableScoreKeys]);

  return (
    <>
      <div>
        <Select<ScoreOption>
          options={availableScoreKeys}
          value={selectedScoreKey}
          onChange={option => {
            if (option) {
              onUpdate(filter.id, {scoreKey: option.value});
            }
          }}
          placeholder="Select score"
        />
      </div>
      <div>
        <SelectOperator
          options={NUMERIC_OPERATOR_OPTIONS}
          value={filter.operator}
          onSelectOperator={(operator: string) => {
            onUpdate(filter.id, {operator});
          }}
        />
      </div>
      <div>
        <TextField
          type="number"
          value={String(filter.value)}
          onChange={value => {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              onUpdate(filter.id, {value: numValue});
            }
          }}
          size="small"
        />
      </div>
      <div className="flex items-center justify-center">
        <Tooltip
          trigger={
            <IconButton onClick={() => onRemove(filter.id)}>
              <Icon name="delete" />
            </IconButton>
          }
          content="Remove filter"
        />
      </div>
    </>
  );
};
